import { FieldValues } from 'react-hook-form'

import { getObjectDiff } from '@bettermode/common/utils'
import { ClientError, ErrorResponse } from '@tribeplatform/gql-client/lib'

export const extractFieldsErrors = (
  error: ClientError,
): Record<string, { message: string; itemIndex: number }> =>
  error?.response?.errors?.reduce((errors, currentError) => {
    const [fieldKey, failingItemIndex] = currentError?.field?.split('.')
    if (fieldKey === 'fields') {
      return {}
    }

    return {
      ...errors,
      [fieldKey]: {
        message: currentError.message,
        ...(failingItemIndex
          ? { itemIndex: parseInt(failingItemIndex, 10) }
          : {}),
      },
    }
  }, {})

export const flattenFormSubmitErrors = (
  error: ClientError,
): ErrorResponse[] => {
  const extractSubErrors = (error: ErrorResponse): ErrorResponse[] => {
    if (error.errors) {
      const subErrors = error.errors.flatMap(extractSubErrors)
      return [error, ...subErrors]
    }

    return [error]
  }

  return error.response?.errors?.flatMap(extractSubErrors) ?? []
}

/**
 * RHF submits all fields data on submit, regardless of whether they are dirty or not.
 * this function is used to get the updated fields from the submitted values.
 */
export const getFormUpdatedFields = <T extends FieldValues>(
  defaultValues: Partial<T>,
  submittedValues: Partial<T>,
): Partial<T> => {
  const diff = getObjectDiff(defaultValues, submittedValues)

  return Object.keys(defaultValues).reduce((acc, currentField) => {
    /**
     * If an inner field is changed, we need to include the top level parent field in the updated fields.
     * The diff returns an array for each changed field. The first item in the path array is the top level parent field.
     */
    const isInDiff = diff.some(item => item.path.at(0) === currentField)

    if (!isInDiff) {
      return acc
    }

    return {
      ...acc,
      [currentField]: submittedValues[currentField],
    }
  }, {} as Partial<T>)
}
